/**
 * One capability, with a worked example of it.
 *
 * ROUTED RATHER THAN EXPANDED IN PLACE: "here is how the thing you asked about actually works"
 * is a link somebody sends, into a procurement thread or beside an RFP answer. READABLE WITHOUT
 * SIGNING IN, like the catalogue it comes from. THE TRAP IS THE POINT OF THE PAGE, and it is the
 * last thing rather than a footnote because it is what a reader cannot get from a feature list.
 * Modelled on t4's page.
 */
import React from "react";
import {Box, Chip, Paper, Typography} from "@mui/material";
import {ArrowBackOutlined, LaunchOutlined, MenuBookOutlined} from "@mui/icons-material";
import {Link as RouterLink, useParams} from "react-router-dom";

import {BY_SLUG, GROUP_OF, STATUS_LABEL} from "../data/capabilities";
import {EXAMPLE_BY_SLUG, Sample} from "../data/capabilityExamples";
import {White, Blue3, Grey2, Grey3, Grey5, Grey7, Grey17} from "../theme/styles";

function Mono({sample}: {sample: Sample}) {
    return (
        <Box sx={{mt: 2.5}}>
            <Typography sx={{fontSize: 12.5, color: Grey7, mb: 0.75}}>{sample.caption}</Typography>
            {sample.kind === "quote" ? (
                <Box sx={{borderLeft: `3px solid ${Blue3}`, pl: 2, py: 0.5}}>
                    <Typography sx={{fontSize: 14, lineHeight: 1.65, color: Grey17}}>{sample.body}</Typography>
                </Box>
            ) : (
                <Box component="pre" sx={{margin: 0, padding: "12px 14px", borderRadius: 1, backgroundColor: Grey2, border: `1px solid ${Grey3}`,
                                          fontSize: 12.5, lineHeight: 1.6, color: Grey17, overflowX: "auto", whiteSpace: "pre"}}>
                    {sample.body}
                </Box>
            )}
        </Box>
    );
}

export default function CapabilityExample() {
    const {slug} = useParams();
    const capability = slug ? BY_SLUG[slug] : undefined;
    const example = slug ? EXAMPLE_BY_SLUG[slug] : undefined;

    if (!capability || !example) {
        return (
            <Box sx={{maxWidth: 820, mx: "auto", px: 3, py: 8}}>
                <Typography sx={{fontSize: 22, fontWeight: 600}}>No example at this address</Typography>
                <Typography sx={{fontSize: 15, color: Grey7, mt: 1.5, lineHeight: 1.7}}>
                    The link is out of date, or names a capability that is no longer in the catalogue.
                </Typography>
                <Typography component={RouterLink} to="/capabilities" sx={{display: "inline-block", mt: 3, fontSize: 15, color: Blue3}}>
                    Back to everything this does
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{backgroundColor: White}}>
            <Box sx={{maxWidth: 880, mx: "auto", px: 3, py: {xs: 5, md: 7}}}>
                {capability.status !== "built" ? (
                    <Box sx={{mb: 4, p: 2.5, border: "1px solid #d32f2f", borderRadius: 1, backgroundColor: "#FDECEA"}}>
                        <Typography sx={{fontSize: 14, color: Grey17, lineHeight: 1.65}}>
                            <strong>On the roadmap, not available yet.</strong> This example describes how the
                            capability will work when it ships. Nothing on this page can be done in the product today.
                        </Typography>
                    </Box>
                ) : null}
                <Box component={RouterLink} to="/capabilities"
                     sx={{display: "inline-flex", alignItems: "center", gap: 0.75, fontSize: 13.5, color: Grey7, textDecoration: "none", "&:hover": {color: Blue3}}}>
                    <ArrowBackOutlined sx={{fontSize: 16}}/>
                    {GROUP_OF[capability.slug]}
                </Box>

                <Box sx={{display: "flex", alignItems: "flex-start", gap: 2, mt: 2}}>
                    <Box sx={{color: Blue3, pt: 0.5}}>{capability.icon}</Box>
                    <Box sx={{flex: 1}}>
                        <Typography component="h1" sx={{fontSize: {xs: 26, md: 32}, fontWeight: 600, lineHeight: 1.2, color: Grey17}}>
                            {capability.name}
                        </Typography>
                        <Chip size="small" label={STATUS_LABEL[capability.status]} sx={{mt: 1.25, fontSize: 12, backgroundColor: Grey2}}/>
                    </Box>
                </Box>

                <Typography sx={{fontSize: 16, lineHeight: 1.75, color: Grey7, mt: 3}}>{capability.text}</Typography>

                <Typography component="h2" sx={{fontSize: 13, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: Grey5, mt: 6}}>
                    An example
                </Typography>
                <Typography sx={{fontSize: 17, lineHeight: 1.7, color: Grey17, mt: 1.5}}>{example.scenario}</Typography>

                <Box sx={{mt: 4}}>
                    {example.steps.map((step, index) => (
                        <Box key={index} sx={{display: "flex", gap: 2.5, pb: 3, borderBottom: index === example.steps.length - 1 ? "none" : `1px solid ${Grey2}`, mb: 3}}>
                            <Box sx={{flex: "0 0 26px", height: 26, borderRadius: "50%", backgroundColor: Grey2, color: Grey7, fontSize: 13,
                                      display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600}}>
                                {index + 1}
                            </Box>
                            <Box sx={{flex: 1}}>
                                <Typography sx={{fontSize: 15, fontWeight: 500, color: Grey17, lineHeight: 1.6}}>{step.does}</Typography>
                                <Typography sx={{fontSize: 15, color: Grey7, lineHeight: 1.7, mt: 0.75}}>{step.then}</Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>

                {(example.samples ?? []).map((sample, index) => <Mono key={index} sample={sample}/>)}

                <Paper elevation={0} sx={{mt: 6, p: 3, border: `1px solid ${Grey3}`, borderRadius: 1, backgroundColor: Grey2}}>
                    <Typography sx={{fontSize: 13, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: Grey5}}>
                        What surprises people
                    </Typography>
                    <Typography sx={{fontSize: 16, fontWeight: 600, color: Grey17, mt: 1}}>{example.trap.heading}</Typography>
                    <Typography sx={{fontSize: 15, color: Grey17, lineHeight: 1.75, mt: 1}}>{example.trap.body}</Typography>
                </Paper>

                {(example.api || example.guide) ? (
                    <Box sx={{mt: 4, display: "flex", flexDirection: "column", gap: 1.25}}>
                        {example.api ? (
                            <Box sx={{display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap"}}>
                                <LaunchOutlined sx={{fontSize: 16, color: Grey5}}/>
                                <Typography sx={{fontSize: 13.5, color: Grey7}}>From a pipeline:</Typography>
                                <Typography component="code" sx={{fontSize: 13, color: Grey17, backgroundColor: Grey2, px: 1, py: 0.25, borderRadius: 0.5}}>
                                    {example.api}
                                </Typography>
                            </Box>
                        ) : null}
                        {example.guide ? (
                            <Box component="a" href={`/docs/${example.guide.file}`} target={example.guide.file} rel="noreferrer"
                                 sx={{display: "inline-flex", alignItems: "center", gap: 1, fontSize: 13.5, color: Blue3, textDecoration: "none", "&:hover": {textDecoration: "underline"}}}>
                                <MenuBookOutlined sx={{fontSize: 16}}/>
                                {example.guide.label}
                            </Box>
                        ) : null}
                    </Box>
                ) : null}

                <Typography component={RouterLink} to="/capabilities"
                            sx={{display: "inline-block", mt: 6, fontSize: 15, color: Blue3, textDecoration: "none", "&:hover": {textDecoration: "underline"}}}>
                    &larr; Everything this does
                </Typography>
            </Box>
        </Box>
    );
}
